import { type ReactNode, useLayoutEffect, useState } from 'react';
import { Flipped } from 'react-flip-toolkit';
import { useTranslate } from 'react-polyglot';

import {
  BiggerSubtitle,
  BigSubtitle,
  Breakpoint,
  useBreakpoint,
} from '@genially/design-system';
import type { PlayerData } from '@genially/p2p-lib';
import { useResizeObserver2 } from '@genially/ui/build/hooks/useResizeObserver2';

import { Size as PlayerSize } from './components/Player/constants';
import { Player } from './components/Player/Player';
import { Direction } from './constants';
import {
  EmptyWrapper,
  PlayerWrapper,
  StyledFlipper,
  WaittingUsers,
  Wrapper,
} from './PlayersList.styled';

interface PlayersListProps {
  players: PlayerData[];
  Icon?: ReactNode;
  dangerOnHover?: boolean;
  direction?: Direction;
  playerWidthFull?: boolean;
  forcedPlayerSize?: PlayerSize;
  centerAligned?: boolean;
  disableEnterAnimationOnLoad?: boolean;
  onPlayerClick?: (player: PlayerData) => void;
}

const PlayersListInternal = ({
  players,
  Icon,
  dangerOnHover,
  direction = Direction.Row,
  playerWidthFull = false,
  forcedPlayerSize,
  centerAligned = false,
  disableEnterAnimationOnLoad = false,
  onPlayerClick,
}: PlayersListProps) => {
  const t = useTranslate();

  const [internalPlayerSize, setInternalPlayerSize] = useState(Player.Size.Medium);
  const playerSize = forcedPlayerSize ?? internalPlayerSize;

  const [disableEnterAnimation, setDisableEnterAnimation] = useState(
    disableEnterAnimationOnLoad,
  );

  const {
    element: contentRef,
    setRef: setContentRef,
    elementRect: contentRect,
  } = useResizeObserver2<HTMLDivElement>();

  const { isDownwards } = useBreakpoint();
  const isMobile = isDownwards(Breakpoint.xs);

  const handlePlayerClick = (player: PlayerData) => () => {
    onPlayerClick?.(player);
  };

  const handleFlipperComplete = () => {
    if (disableEnterAnimation) {
      setDisableEnterAnimation(false);
    }
  };

  useLayoutEffect(
    function updatePlayerSize() {
      if (contentRef && contentRect) {
        if (playerSize === Player.Size.Small) {
          return;
        }

        if (contentRef.scrollHeight > contentRef.clientHeight) {
          if (internalPlayerSize === Player.Size.Large) {
            const timerId = setTimeout(() => {
              setInternalPlayerSize(Player.Size.Medium);
            }, 200);
            return () => clearTimeout(timerId);
          }

          setInternalPlayerSize(Player.Size.Small);
        }
      }
    },
    [contentRef, contentRect, internalPlayerSize, playerSize],
  );

  return (
    <Wrapper
      ref={setContentRef}
      $centerAligned={
        centerAligned
          ? players.length === 0 || (!isMobile && playerSize !== Player.Size.Small)
          : false
      }
      $direction={direction}
    >
      {players.length > 0 ? (
        <StyledFlipper
          flipKey={players.map(({ id }) => id).join('')}
          $size={playerSize}
          $fullWidth={playerWidthFull}
          onComplete={handleFlipperComplete}
        >
          {players.map(({ id, username }) => (
            <Flipped flipId={id} key={id}>
              <PlayerWrapper $fullWidth={playerWidthFull}>
                <Player
                  name={username}
                  size={playerSize}
                  Icon={Icon}
                  dangerOnHover={dangerOnHover}
                  disableEnterAnimation={disableEnterAnimation}
                  onClick={
                    onPlayerClick ? handlePlayerClick({ id, username }) : undefined
                  }
                />
              </PlayerWrapper>
            </Flipped>
          ))}
        </StyledFlipper>
      ) : (
        <EmptyWrapper>
          <WaittingUsers
            with={playerSize === PlayerSize.Small ? BigSubtitle : BiggerSubtitle}
            on={{ [Breakpoint.xs]: BigSubtitle }}
          >
            {t('__new.play.creatorLobby.participantJoining.text')}
          </WaittingUsers>
        </EmptyWrapper>
      )}
    </Wrapper>
  );
};

PlayersListInternal.Direction = Direction;
PlayersListInternal.PlayerSize = Player.Size;

export const PlayersList = PlayersListInternal;
